import type { FinancialWorkNavId, HumanAskKind } from "./types";
import { HUMAN_ASK_KINDS } from "./types";
import {
  BILLIN_SCROLL_TO_RECEIVABLE,
  BILLIN_SCROLL_TO_TRANSACTIONS,
} from "@/lib/billinGoRoutes";

export const FINANCIAL_WORK_NAV_PARAM = "fw";
/** Harness-only: force the load-failed empty state (not financial truth). */
export const FINANCIAL_WORK_LOAD_FAIL_PARAM = "fwLoadFail";
/** Pointer to the id query key, e.g. `object=receivable_batch_id`. */
export const FINANCIAL_WORK_OBJECT_PARAM = "object";
/** Pointer to the revision query key, e.g. `rev=rev_id`. */
export const FINANCIAL_WORK_REV_PARAM = "rev";
export const FINANCIAL_WORK_REV_ID_PARAM = "rev_id";
/** Actual id query keys — `object` names which one to read. */
export const FINANCIAL_WORK_OBJECT_ID_PARAMS = {
  receivable_batch: "receivable_batch_id",
  receivable: "receivable_id",
} as const;

export function objectIdParamForKind(kind: string): string {
  const mapped =
    FINANCIAL_WORK_OBJECT_ID_PARAMS[
      kind as keyof typeof FINANCIAL_WORK_OBJECT_ID_PARAMS
    ];
  return mapped ?? `${kind.trim()}_id`;
}

export function kindFromObjectIdParam(key: string): string | null {
  const trimmed = key.trim();
  if (!trimmed) return null;
  for (const [kind, param] of Object.entries(FINANCIAL_WORK_OBJECT_ID_PARAMS)) {
    if (param === trimmed) return kind;
  }
  if (trimmed === FINANCIAL_WORK_REV_ID_PARAM) return null;
  if (trimmed.endsWith("_id") && trimmed.length > 3) {
    return trimmed.slice(0, -3);
  }
  return null;
}
/** Opaque ActionDirective id from LINE postback (optional continuity hint). */
export const FINANCIAL_WORK_ACTION_PARAM = "fwAction";
/** Queue filter id to restore after detail close (`all` | bucket id). */
export const FINANCIAL_WORK_FILTER_PARAM = "fwFilter";
/** Scroll to a batch set in the work list after closing ชุดยอดค้าง detail. */
export const FINANCIAL_WORK_SCROLL_BATCH_PARAM = "fwScrollBatch";
/** Detail panel focus: `outstanding` | `children` (R0-05 / R0-07). */
export const FINANCIAL_WORK_PANEL_PARAM = "fwPanel";

export const WORK_BUCKET_LABELS: Record<string, string> = {
  needs_action: "ต้องให้คุณทำ",
  waiting: "รับบางส่วน",
  needs_review: "ต้องตรวจ",
  risk: "เสี่ยง/เกินกำหนด",
  recently_closed: "ปิดล่าสุด",
};

export function isFinancialWorkNavId(value: string | null | undefined): value is FinancialWorkNavId {
  return (
    value === "work" ||
    value === "data" ||
    value === "sources" ||
    value === "periods"
  );
}

/**
 * Resolve primary IA tab.
 * Deep links into the legacy 5-tab data views stay on `data`.
 */
export function resolveFinancialWorkNav(args: {
  fw: string | null;
  workerId: string | null;
  recordId: string | null;
  highlight: string | null;
  scrollTo: string | null;
  modal: string | null;
  objectRef?: string | null;
}): FinancialWorkNavId {
  if (isFinancialWorkNavId(args.fw)) {
    return args.fw;
  }
  if (args.objectRef?.trim()) return "work";

  const scrollTo = args.scrollTo?.trim() ?? "";
  const hasDataDeepLink = Boolean(
    args.workerId?.trim() ||
      args.recordId?.trim() ||
      args.highlight?.trim() ||
      args.modal?.trim() ||
      scrollTo === BILLIN_SCROLL_TO_TRANSACTIONS ||
      scrollTo === BILLIN_SCROLL_TO_RECEIVABLE,
  );

  if (hasDataDeepLink) return "data";
  // Default to 「ข้อมูล」 so รายรับ / สลิปค้าง / ยอดค้าง stay visible.
  // 「งานเงิน」 opens via fw=work or a Financial Work object deep link.
  return "data";
}

export function formatAmountMinor(
  amountMinor: number,
  currency = "THB",
): string {
  const major = amountMinor / 100;
  try {
    return new Intl.NumberFormat("th-TH", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(major);
  } catch {
    return `${major.toLocaleString("th-TH")} ${currency}`;
  }
}

/** One shop-facing status for queue cards and detail — avoid stacking ต้องดำเนินการ + ยังค้างชำระ. */
export function askKindFromCard(card: {
  contextRows?: Array<{ key: string; value: string | null }>;
}): HumanAskKind | null {
  const value = card.contextRows?.find((row) => row.key === "ask_kind")?.value;
  if (value && (HUMAN_ASK_KINDS as readonly string[]).includes(value)) {
    return value as HumanAskKind;
  }
  return null;
}

export function isSlipOwnerAskCard(card: {
  title?: string;
  contextRows?: Array<{ key: string; value: string | null }>;
  consequenceRows?: Array<unknown>;
}): boolean {
  if (askKindFromCard(card)) return true;
  if ((card.title || "").includes("เป็นของยอดไหน")) return true;
  return (card.consequenceRows?.length ?? 0) >= 2 && Boolean(
    card.contextRows?.some((row) => row.key === "ask_kind"),
  );
}

export function workStatusLabel(card: {
  surfaceState: string;
  subtitle?: string;
  nextStep?: { label?: string };
  requirementStatus?: { complete?: boolean };
  title?: string;
  contextRows?: Array<{ key: string; value: string | null }>;
  consequenceRows?: Array<unknown>;
}): string {
  if (card.requirementStatus?.complete || card.surfaceState === "closed") {
    return "ปิดยอดแล้ว";
  }
  if (isSlipOwnerAskCard(card)) return "รอการตัดสินใจ";
  if (card.surfaceState === "needs_review") return "ต้องตรวจ";
  if (card.surfaceState === "risk") return "เกินกำหนด";
  if (card.surfaceState === "waiting") return "รอสลิป";
  const next = card.nextStep?.label ?? "";
  if (next.includes("รอสลิป")) return "รอสลิป";
  if ((card.subtitle || "").includes("ยังค้าง")) return "รอสลิป";
  return surfaceStateLabel(card.surfaceState);
}

export function surfaceStateLabel(state: string): string {
  switch (state) {
    case "needs_action":
      return "ต้องดำเนินการ";
    case "waiting":
      return "รอสลิป";
    case "needs_review":
      return "ต้องตรวจ";
    case "risk":
      return "เกินกำหนด";
    case "closed":
      return "ปิดยอดแล้ว";
    case "corrected":
      return "มีการแก้ไข";
    case "processing":
      return "กำลังอัปเดต";
    default:
      return state;
  }
}

/** Shop-facing title: drop internal “ลูกค้า” prefix. */
export function displayWorkTitle(title: string | undefined | null): string {
  const t = String(title ?? "").trim();
  return t.replace(/^ลูกหนี้\s+/, "").trim() || t;
}

/** Slip row title uses the transferor on the slip, not the receivable customer. */
export function slipTransferLabel(
  slipPartyName: string | undefined | null,
  fallback: string,
): string {
  const name =
    String(slipPartyName ?? "").trim() || String(fallback ?? "").trim();
  return `สลิปโอนของ ${name}`;
}

/** Badge copy/tone for the work-detail drawer (canonical record surface). */
export function workDetailBadge(card: {
  surfaceState: string;
  subtitle?: string;
  nextStep?: { label?: string };
  requirementStatus?: { complete?: boolean };
  title?: string;
  contextRows?: Array<{ key: string; value: string | null }>;
  consequenceRows?: Array<unknown>;
}): { title: string; tone: "ok" | "warn" | "danger" } {
  if (card.requirementStatus?.complete || card.surfaceState === "closed") {
    return { title: "บันทึกแล้ว", tone: "ok" };
  }
  const next = card.nextStep?.label ?? "";
  const status = workStatusLabel(card);
  if (
    isSlipOwnerAskCard(card) ||
    status === "ต้องเลือกยอด" ||
    status === "รอการตัดสินใจ"
  ) {
    return { title: "รอการตัดสินใจ", tone: "warn" };
  }
  if (
    card.surfaceState === "risk" ||
    next.includes("ตรวจบัญชี") ||
    status === "เกินกำหนด"
  ) {
    return { title: "ต้องระวัง", tone: "danger" };
  }
  if (
    status === "รอสลิป" ||
    next.includes("จับคู่") ||
    next.includes("เลือกยอด")
  ) {
    return { title: "รอจับคู่", tone: "warn" };
  }
  if (status === "ต้องตรวจ") return { title: "ต้องตรวจ", tone: "warn" };
  return { title: status, tone: "warn" };
}

export function isReceivableBatchCard(card: {
  objectRef?: { kind?: string };
}): boolean {
  return card.objectRef?.kind === "receivable_batch";
}

export function normalizeBatchId(raw: string | null | undefined): string | null {
  const id = String(raw ?? "").trim();
  if (!id) return null;
  if (id.startsWith("batch:")) {
    const stripped = id.slice("batch:".length).trim();
    return stripped || id;
  }
  return id;
}

export function isBacklogChildPaid(row: {
  status?: string;
  remainingAmountMinor?: number;
}): boolean {
  if (row.status === "paid") return true;
  return row.remainingAmountMinor != null && row.remainingAmountMinor <= 0;
}

export function cardBatchId(card: {
  contextRows?: Array<{ key: string; value: string | null }>;
}): string | null {
  const raw = card.contextRows?.find((row) => row.key === "batch_id")?.value;
  return normalizeBatchId(raw);
}

export function cardBatchSize(card: {
  contextRows?: Array<{ key: string; value: string | null }>;
  backlogChildren?: Array<unknown>;
  outstanding?: { openCount?: number };
}): number {
  const raw = card.contextRows?.find((row) => row.key === "batch_size")?.value;
  const parsed = Number(raw);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  const children = card.backlogChildren?.length ?? 0;
  if (children > 0) return children;
  const open = card.outstanding?.openCount ?? 0;
  return open > 0 ? open : 0;
}

export function cardBatchClosed(card: {
  contextRows?: Array<{ key: string; value: string | null }>;
}): number {
  const raw = card.contextRows?.find((row) => row.key === "batch_closed")?.value;
  const parsed = Number(raw);
  if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  return 0;
}

/** Paid/open child that still belongs to a announced set — not a true เดี่ยว. */
export function isFromBatchCard(card: {
  objectRef?: { kind?: string };
  contextRows?: Array<{ key: string; value: string | null }>;
}): boolean {
  return !isReceivableBatchCard(card) && Boolean(cardBatchId(card));
}

function isReceivableSetMember(card: { objectRef?: { kind?: string } }): boolean {
  return card.objectRef?.kind === "receivable";
}

export function isSharedBatchCard(card: {
  objectRef?: { kind?: string };
  contextRows?: Array<{ key: string; value: string | null }>;
  backlogChildren?: Array<unknown>;
  outstanding?: { openCount?: number };
}): boolean {
  if (isReceivableBatchCard(card) || !isReceivableSetMember(card)) return false;
  return Boolean(cardBatchId(card)) && cardBatchSize(card) >= 2;
}

export function isClosedWorkCard(card: {
  surfaceState?: string;
  requirementStatus?: { complete?: boolean };
}): boolean {
  return Boolean(card.requirementStatus?.complete || card.surfaceState === "closed");
}

function bestBatchRosterStats(
  cards: Array<{
    backlogChildren?: Array<{ status?: string; remainingAmountMinor?: number }>;
  }>,
): { size: number; paid: number } {
  let best = { size: 0, paid: 0 };
  for (const card of cards) {
    const roster = card.backlogChildren ?? [];
    if (roster.length < 2) continue;
    const paid = roster.filter((row) => isBacklogChildPaid(row)).length;
    const score = roster.length * 100 + paid;
    const bestScore = best.size * 100 + best.paid;
    if (score > bestScore) {
      best = { size: roster.length, paid };
    }
  }
  return best;
}

export function batchClosedCount<T extends {
  surfaceState?: string;
  workPlacement?: { bucket?: string };
  requirementStatus?: { complete?: boolean };
  contextRows?: Array<{ key: string; value: string | null }>;
  backlogChildren?: Array<{ status?: string; remainingAmountMinor?: number }>;
}>(
  cards: T[],
  memberCount = 0,
  opts?: { inferHiddenClosed?: boolean },
): number {
  const serverClosed = Math.max(0, ...cards.map((card) => cardBatchClosed(card)));
  const rosterStats = bestBatchRosterStats(cards);
  const visibleClosed = cards.filter(isClosedWorkCard).length;

  // Full roster is authoritative — open mates off the waiting tab are not "closed".
  if (rosterStats.size >= 2 && memberCount > 0 && rosterStats.size >= memberCount) {
    return Math.min(Math.max(rosterStats.paid, visibleClosed), memberCount);
  }

  const openVisible = cards.filter((card) => !isClosedWorkCard(card)).length;
  const inferredHidden =
    opts?.inferHiddenClosed &&
    serverClosed > 0 &&
    openVisible === cards.length &&
    memberCount > cards.length
      ? Math.min(serverClosed, memberCount - cards.length)
      : 0;
  const closed = Math.max(
    serverClosed,
    rosterStats.paid,
    visibleClosed,
    inferredHidden,
  );
  if (memberCount > 0) return Math.min(closed, memberCount);
  return closed;
}

export function batchSetLabel(memberCount: number, closedCount = 0): string {
  const total = Math.max(0, memberCount);
  const closed = Math.min(Math.max(0, closedCount), total);
  if (total <= 0) return "ชุดรายการเดียวกัน";
  if (closed <= 0) return `ชุดรายการเดียวกัน ${total} รายการ`;
  return `ชุดรายการเดียวกัน ${total} รายการ ปิดแล้ว ${closed} รายการ`;
}

export function batchHeaderClosedCount<T extends {
  surfaceState?: string;
  workPlacement?: { bucket?: string };
  requirementStatus?: { complete?: boolean };
  contextRows?: Array<{ key: string; value: string | null }>;
  backlogChildren?: Array<{ status?: string }>;
}>(
  segment: { cards: T[]; memberCount: number; closedCount: number },
  opts?: { inferHiddenClosed?: boolean },
): number {
  return Math.max(
    segment.closedCount,
    batchClosedCount(segment.cards, segment.memberCount, opts),
  );
}

export type BatchRowSegment<T> = {
  batchId: string | null;
  memberCount: number;
  closedCount: number;
  cards: T[];
};

export function clusterCardsByBatch<T extends {
  objectRef?: { kind?: string };
  contextRows?: Array<{ key: string; value: string | null }>;
  backlogChildren?: Array<unknown>;
  outstanding?: { openCount?: number };
}>(cards: T[]): T[] {
  const byBatch = new Map<string, T[]>();
  for (const card of cards) {
    const id = isSharedBatchCard(card) ? cardBatchId(card) : null;
    if (!id) continue;
    const list = byBatch.get(id) ?? [];
    list.push(card);
    byBatch.set(id, list);
  }
  const used = new Set<T>();
  const out: T[] = [];
  for (const card of cards) {
    if (used.has(card)) continue;
    const id = isSharedBatchCard(card) ? cardBatchId(card) : null;
    const group = id ? byBatch.get(id) : null;
    if (group && group.length >= 2) {
      for (const mate of group) {
        if (used.has(mate)) continue;
        out.push(mate);
        used.add(mate);
      }
    } else {
      out.push(card);
      used.add(card);
    }
  }
  return out;
}

export function segmentCardsByBatch<T extends {
  objectRef?: { kind?: string };
  surfaceState?: string;
  workPlacement?: { bucket?: string };
  requirementStatus?: { complete?: boolean };
  contextRows?: Array<{ key: string; value: string | null }>;
  backlogChildren?: Array<{ status?: string; remainingAmountMinor?: number }>;
  outstanding?: { openCount?: number };
}>(cards: T[], opts?: { closedView?: boolean; allView?: boolean }): BatchRowSegment<T>[] {
  const clustered = clusterCardsByBatch(cards);
  const segments: BatchRowSegment<T>[] = [];
  for (const card of clustered) {
    const batchId = isSharedBatchCard(card) ? cardBatchId(card) : null;
    const memberCount = batchId ? cardBatchSize(card) : 0;
    const closedCount = batchId ? batchClosedCount([card], memberCount) : 0;
    const last = segments[segments.length - 1];
    if (batchId && last && last.batchId === batchId) {
      last.cards.push(card);
      last.memberCount = Math.max(last.memberCount, memberCount);
      last.closedCount = Math.max(last.closedCount, closedCount);
      continue;
    }
    segments.push({
      batchId,
      memberCount,
      closedCount,
      cards: [card],
    });
  }
  return segments
    .flatMap((segment): BatchRowSegment<T>[] => {
      if (!segment.batchId) return [segment];
      const allCards = segment.cards;
      if (opts?.closedView) {
        const closedCards = allCards.filter(isClosedWorkCard);
        const visible = closedCards.length > 0 ? closedCards : allCards;
        const closedCount = batchClosedCount(visible, segment.memberCount);
        // Keep the set header even when only one closed mate is on the page —
        // memberCount/closedCount still describe the full announced set.
        return [{ ...segment, cards: visible, closedCount }];
      }
      const openCards = allCards.filter((card) => !isClosedWorkCard(card));
      const closedCards = allCards.filter(isClosedWorkCard);
      const closedCount = batchClosedCount(allCards, segment.memberCount);
      // Keep the set header when a shared batch has only one open row left
      // (e.g. "ชุดรายการเดียวกัน 3 รายการ ปิดแล้ว 2 รายการ").
      const openSegment: BatchRowSegment<T> = {
        ...segment,
        cards: openCards,
        closedCount,
      };
      if (!opts?.allView) return [openSegment];
      // ทั้งหมด: keep closed batch mates that open-tabs intentionally hide.
      return [
        openSegment,
        ...closedCards.map((card) => ({
          batchId: null,
          memberCount: 0,
          closedCount: 0,
          cards: [card],
        })),
      ];
    })
    .filter((segment) => segment.cards.length > 0);
}

export function partitionWorkCards<T extends {
  objectRef?: { kind?: string };
  contextRows?: Array<{ key: string; value: string | null }>;
}>(cards: T[]): { batches: T[]; fromBatch: T[]; singles: T[] } {
  const batches: T[] = [];
  const fromBatch: T[] = [];
  const singles: T[] = [];
  for (const card of cards) {
    if (isReceivableBatchCard(card)) batches.push(card);
    else if (isFromBatchCard(card)) fromBatch.push(card);
    else singles.push(card);
  }
  return { batches, fromBatch, singles };
}

export function amountBasisLabel(basis: string | undefined, opts?: { complete?: boolean }): string {
  if (opts?.complete) return "จ่ายครบแล้ว";
  switch (basis) {
    case "remaining":
      return "ยอดที่ยังค้าง";
    case "gross":
      return "ยอดเต็ม";
    case "cash":
      return "ยอดที่โอนมา";
    case "due":
      return "ยอดที่ต้องชำระ";
    case "aggregate":
      return "ยอดรวมชุดนี้";
    default:
      return "";
  }
}

/** Translate server ledger/status tokens shown in context rows. */
function displayLastDecision(value: string): string {
  switch (value) {
    case "park":
    case "user_parked":
      return "พักไว้ก่อน";
    case "user_rejected":
      return "ยังไม่ใช่";
    case "user_confirmed":
      return "ยืนยันแล้ว";
    case "create_artifact":
      return "จับคู่ยอดอัตโนมัติ";
    case "clarify":
    case "confirm":
      return "รอยืนยัน";
    case "select":
      return "เลือกรายการ";
    case "auto":
      return "จับคู่ยอดอัตโนมัติ";
    default:
      return value;
  }
}

export function displayContextValue(
  key: string,
  value: string | null | undefined,
): string {
  const v = String(value ?? "").trim();
  if (key === "last_decision") {
    return displayLastDecision(v);
  }
  if (key === "status" || key === "state") {
    switch (v) {
      case "paid":
      case "closed":
        return "จ่ายครบแล้ว";
      case "open":
        return "ยังค้างชำระ";
      case "partially_paid":
      case "waiting_slip":
        return "จ่ายบางส่วน รอสลิป";
      case "needs_review":
        return "ต้องตรวจ";
      default:
        return v;
    }
  }
  return v;
}
